import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { CustomRole, CreateRoleInput } from '../types';

const INITIAL_FALLBACK_ROLES: CustomRole[] = [
  {
    id: 'role1',
    name: 'Administrator',
    description: 'Full uninhibited system and tenancy administration',
    permissions: ['*'],
    user_count: 2,
    is_system: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'role2',
    name: 'Operations Manager',
    description: 'Departmental head with management scope',
    permissions: ['core.*', 'crm.*', 'ops.*'],
    user_count: 4,
    is_system: false,
    created_at: new Date().toISOString(),
  },
  {
    id: 'role3',
    name: 'Standard Staff',
    description: 'Standard day-to-day read/write business privileges',
    permissions: ['core.read', 'core.write', 'crm.read'],
    user_count: 15,
    is_system: false,
    created_at: new Date().toISOString(),
  },
];

export function useRoles() {
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await coreApi.get<CustomRole[]>('/roles');
      if (Array.isArray(data) && data.length > 0) {
        setRoles(data);
      } else {
        setRoles(INITIAL_FALLBACK_ROLES);
      }
    } catch {
      setRoles(INITIAL_FALLBACK_ROLES);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const createRole = async (input: CreateRoleInput) => {
    try {
      const created = await coreApi.post<CustomRole>('/roles', input);
      setRoles((prev) => [created || { ...input, id: `role-${Date.now()}` }, ...prev]);
    } catch {
      const local: CustomRole = {
        ...input,
        id: `role-${Date.now()}`,
        user_count: 0,
        is_system: false,
        created_at: new Date().toISOString(),
      };
      setRoles((prev) => [local, ...prev]);
    }
  };

  const updateRole = async (id: string, input: Partial<CustomRole>) => {
    try {
      const updated = await coreApi.put<CustomRole>(`/roles/${id}`, input);
      setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated, ...input } : r)));
    } catch {
      setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, ...input } : r)));
    }
  };

  const deleteRole = async (id: string) => {
    try {
      await coreApi.delete(`/roles/${id}`);
    } catch {
      // ignore
    }
    setRoles((prev) => prev.filter((r) => r.id !== id));
  };

  return {
    roles,
    isLoading,
    error,
    createRole,
    updateRole,
    deleteRole,
    reload: fetchRoles,
  };
}

export default useRoles;
